import { ReactElement, useEffect } from 'react'
import { Box, FormControl, InputLabel, MenuItem, Select } from '@mui/material'
import { useIfcFiles } from '@editor/hooks'
import { useControlStore } from '@editor/stores'

interface Props {
  /** Hide the "IFC-Modell" floating label. */
  hideLabel?: boolean
  /** Use the underlined "standard" variant instead of the outlined box (e.g. in a toolbar). */
  standardVariant?: boolean
  /** Open the menu upwards — for bars docked to the bottom of the viewport. */
  openUpwards?: boolean
  /** Shrink to a narrow, small-font dropdown for tight panels (e.g. the storey controls header). */
  compact?: boolean
}

/**
 * Reusable dropdown to select the active IFC/GLB model.
 * Syncs with the editor control store (activeIfcFileGuid).
 * Defaults to the first model when files exist and none is selected.
 */
const IFCFileSelect = ({
  hideLabel = false,
  standardVariant = false,
  openUpwards = false,
  compact = false,
}: Props): ReactElement => {
  const variant = standardVariant ? 'standard' : 'outlined'
  const glbFiles = useIfcFiles()
  const activeIfcFileGuid = useControlStore(state => state.activeIfcFileGuid)
  const setActiveIfcFileGuid = useControlStore(state => state.setActiveIfcFileGuid)

  useEffect(() => {
    if (glbFiles.length === 0) return
    const currentInList =
      activeIfcFileGuid != null && glbFiles.some(f => f.guid === activeIfcFileGuid)
    if (!currentInList) {
      setActiveIfcFileGuid(glbFiles[0].guid)
    }
  }, [glbFiles, activeIfcFileGuid, setActiveIfcFileGuid])

  const value = activeIfcFileGuid

  return (
    <Box display="flex" flex={compact ? undefined : 1}>
      <FormControl
        variant={variant}
        size="small"
        fullWidth={!compact}
        sx={{ minWidth: compact ? 90 : 200 }}
      >
        {!hideLabel && (
          <InputLabel id="ifc-file-select-label" shrink>
            IFC-Modell
          </InputLabel>
        )}
        <Select
          variant={variant}
          labelId={hideLabel ? undefined : 'ifc-file-select-label'}
          id="ifc-file-select"
          value={value || ''}
          label={hideLabel ? undefined : 'IFC-Modell'}
          onChange={e => {
            e.stopPropagation()
            const next = e.target.value
            setActiveIfcFileGuid(next)
          }}
          disabled={glbFiles.length === 0}
          sx={compact ? { '& .MuiSelect-select': { py: 0.25, fontSize: '0.75rem' } } : undefined}
          MenuProps={
            openUpwards
              ? {
                  // Portal the menu so it floats above a bottom-docked toolbar (an inline,
                  // non-portaled menu would grow the bar upward instead of overlaying).
                  anchorOrigin: { vertical: 'top', horizontal: 'left' },
                  transformOrigin: { vertical: 'bottom', horizontal: 'left' },
                }
              : {
                  // making the menu a direct child if the select is required to enable
                  // correct mouseenter / leave behavior, disablePortal: true / false is
                  // possible as the menu inherits from Popper
                  // This is also needed to prevent the click on the dropdown from closing the parent widget if there is any
                  disablePortal: true,
                }
          }
          displayEmpty
          renderValue={selected => {
            const selectedFile = glbFiles.find(f => f.guid === selected)
            return selectedFile ? (
              selectedFile.name ?? ''
            ) : (
              <Box component="span" sx={{ color: 'text.disabled' }}>
                Keine IFC-Datei
              </Box>
            )
          }}
        >
          {glbFiles.map(file => (
            <MenuItem
              key={file.guid}
              value={file.guid}
              // Stop propagation needed to prevent the dropdown from closing
              onClick={e => e.stopPropagation()}
            >
              {file.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Box>
  )
}

export default IFCFileSelect
